/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-10-29
 */
import {
  EnvironmentProviders,
  Injectable,
  InjectionToken,
  Injector,
  inject,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
  runInInjectionContext,
} from '@angular/core';
import { Observable, delay, of } from 'rxjs';
import type { ContextoInforme } from './contexto-informe';
import type {
  CatalogoInformes,
  DefinicionInforme,
  ResultadoInforme,
  SolicitudInforme,
} from './informes.model';

/**
 * Quien ejecuta un informe. Es el punto de conexion con la arquitectura de procesamiento (Centro
 * de Tareas, notificaciones, correo y ejecucion en segundo plano): ese ejecutor reemplazara al de
 * diseño sin tocar el lanzador.
 */
export interface EjecutorInformes {
  /**
   * Ejecuta la solicitud.
   *
   * @param solicitud informe y parametros elegidos
   * @returns el resultado cuando termina
   */
  ejecutar(solicitud: SolicitudInforme): Observable<ResultadoInforme>;
}

/**
 * Ejecutor de la etapa de diseño (Carlos, 29-oct-2026: «ahora sol ineresa el diseño»): no llama
 * al servidor; responde al segundo con el nombre del archivo, como el prototipo aprobado.
 */
@Injectable({ providedIn: 'root' })
export class EjecutorInformesDeDiseno implements EjecutorInformes {
  /** @inheritdoc */
  ejecutar(solicitud: SolicitudInforme): Observable<ResultadoInforme> {
    return of({ archivo: `${solicitud.nombre}.${solicitud.parametros.formato}` }).pipe(delay(1000));
  }
}

/** Ejecutor de informes que usa el lanzador; por defecto, el de diseño. */
export const EJECUTOR_INFORMES = new InjectionToken<EjecutorInformes>('EJECUTOR_INFORMES', {
  providedIn: 'root',
  factory: () => inject(EjecutorInformesDeDiseno),
});

/**
 * Orquestador de informes: cada producto registra aqui su catalogo y el lanzador lo pide por el
 * codigo del producto. Arma el contexto de cada informe (el especifico si el informe lo declara,
 * si no el del producto) y entrega la ejecucion al ejecutor.
 */
@Injectable({ providedIn: 'root' })
export class InformesOrquestador {
  private readonly catalogos = new Map<string, CatalogoInformes>();
  private readonly contextos = new Map<string, ContextoInforme>();
  private readonly ejecutor = inject(EJECUTOR_INFORMES);
  private readonly injector = inject(Injector);

  /**
   * Registra (o reemplaza) el catalogo de un producto.
   *
   * @param catalogo catalogo del producto
   */
  registrar(catalogo: CatalogoInformes): void {
    this.catalogos.set(catalogo.producto, catalogo);
    for (const clave of [...this.contextos.keys()]) {
      if (clave.startsWith(`${catalogo.producto}/`)) this.contextos.delete(clave);
    }
  }

  /**
   * Catalogo de un producto.
   *
   * @param producto codigo del producto
   * @returns el catalogo, o `null` si el producto aun no tiene informes
   */
  catalogo(producto: string): CatalogoInformes | null {
    const c = this.catalogos.get(producto);
    return c && c.informes.length ? c : null;
  }

  /**
   * Contexto de un informe; se crea una vez por informe, en el contexto de inyeccion de la
   * aplicacion.
   *
   * @param catalogo catalogo del producto
   * @param definicion informe
   * @returns su contexto (especifico o del producto)
   */
  contexto(catalogo: CatalogoInformes, definicion: DefinicionInforme): ContextoInforme {
    const clave = `${catalogo.producto}/${definicion.id}`;
    let ctx = this.contextos.get(clave);
    if (!ctx) {
      const fabrica = definicion.contexto ?? catalogo.contexto;
      ctx = runInInjectionContext(this.injector, () => fabrica(definicion, catalogo));
      this.contextos.set(clave, ctx);
    }
    return ctx;
  }

  /**
   * Ejecuta un informe con el ejecutor configurado.
   *
   * @param solicitud informe y parametros
   * @returns el resultado
   */
  generar(solicitud: SolicitudInforme): Observable<ResultadoInforme> {
    return this.ejecutor.ejecutar(solicitud);
  }
}

/**
 * Registra el catalogo de informes de un producto al activarse su ruta de informes.
 *
 * @param catalogo catalogo del producto
 * @returns los proveedores de la ruta
 */
export function provideInformes(catalogo: CatalogoInformes): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideEnvironmentInitializer(() => inject(InformesOrquestador).registrar(catalogo)),
  ]);
}
